import { api } from "@services/api";
import type {
  DeliveryHub,
  SpokeChangeRequest,
  SpokeChangeStatus,
  SpokeChangeStatusResponse,
  SpokeChangeType,
} from "@screens/Onboarding/types";

// All endpoints below are gated by requiresTeamLeaderOrCpDb on the server
// (packages/partner-db/routes/leader.js). The CP DB user_token on the axios
// default instance provides the session.

// ─── Backend response shapes ───
interface BackendSpoke {
  cp_id: string;
  name: string;
  formatted_address?: string;
  city?: string;
  state?: string;
  lat?: string | number;
  lng?: string | number;
  distance?: string | number;
}

interface BackendSpokeChangeRequestRow {
  id: number;
  old_cp_db_id: number;
  db_phone_number: string;
  old_cp_id: string;
  new_cp_id: string;
  change_type: SpokeChangeType;
  status: SpokeChangeStatus;
  old_gullak_balance: number | string | null;
  notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

interface BackendGetSpokesResponse {
  current_cp_id: string;
  spokes: BackendSpoke[];
}

interface BackendGetStatusResponse {
  current_cp_id: string;
  spoke_change_type: "TEMPORARY" | "PERMANENT" | null;
  parent_cp_db_id: number | null;
  pending_request: BackendSpokeChangeRequestRow | null;
}

interface BackendSubmitRequestResponse {
  request: BackendSpokeChangeRequestRow;
}

interface BackendCancelResponse {
  request: BackendSpokeChangeRequestRow;
}

// ─── Mappers ───
const mapSpokeToHub = (s: BackendSpoke): DeliveryHub => ({
  id: s.cp_id,
  name: s.name,
  address: s.formatted_address ?? [s.city, s.state].filter(Boolean).join(", "),
  hasIncentive: false,
});

const mapRequest = (r: BackendSpokeChangeRequestRow): SpokeChangeRequest => ({
  id: r.id,
  oldCpDbId: r.old_cp_db_id,
  dbPhoneNumber: r.db_phone_number,
  oldCpId: r.old_cp_id,
  newCpId: r.new_cp_id,
  changeType: r.change_type,
  status: r.status,
  oldGullakBalance:
    r.old_gullak_balance == null ? null : Number(r.old_gullak_balance),
  notes: r.notes,
  createdAt: r.created_at,
  reviewedAt: r.reviewed_at,
  reviewedBy: r.reviewed_by,
});

// ─── Fetch available spokes for a change ───
// The server excludes the caller's current spoke. If lat/lng are passed, results
// are ranked by distance; otherwise the full list is returned.
export const getAvailableSpokes = async (geo?: {
  lat: number;
  lng: number;
}): Promise<{ currentCpId: string; spokes: DeliveryHub[] }> => {
  const { data } = await api.get<BackendGetSpokesResponse>(
    "/cpdb/spoke-change/spokes",
    {
      params: geo ? { lat: geo.lat, lng: geo.lng } : undefined,
    },
  );
  return {
    currentCpId: data.current_cp_id,
    spokes: (data.spokes ?? []).map(mapSpokeToHub),
  };
};

// ─── Current spoke-change status for the caller ───
export const getSpokeChangeStatus =
  async (): Promise<SpokeChangeStatusResponse> => {
    const { data } = await api.get<BackendGetStatusResponse>(
      "/cpdb/spoke-change/status",
    );
    return {
      currentCpId: data.current_cp_id,
      spokeChangeType: data.spoke_change_type,
      parentCpDbId: data.parent_cp_db_id,
      pendingRequest: data.pending_request
        ? mapRequest(data.pending_request)
        : null,
    };
  };

// ─── Submit a spoke-change request ───
export const requestSpokeChange = async (
  newCpId: string,
  changeType: SpokeChangeType,
  notes?: string,
): Promise<SpokeChangeRequest> => {
  const { data } = await api.post<BackendSubmitRequestResponse>(
    "/cpdb/spoke-change/request",
    { new_cp_id: newCpId, change_type: changeType, notes },
  );
  return mapRequest(data.request);
};

// ─── Cancel a pending request ───
export const cancelSpokeChangeRequest = async (
  requestId: number,
): Promise<SpokeChangeRequest> => {
  const { data } = await api.post<BackendCancelResponse>(
    "/cpdb/spoke-change/cancel",
    { request_id: requestId },
  );
  return mapRequest(data.request);
};
